import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const EXAM_PRESETS = [
  'ANNUAL EXAMINATION',
  'HALF YEARLY EXAMINATION',
  'QUARTERLY EXAMINATION',
  'PRE-BOARD EXAMINATION',
  'UNIT TEST - I',
  'UNIT TEST - II',
  'PERIODIC ASSESSMENT'
];

const SAFE_FONT = 'Arial, "Helvetica Neue", Helvetica, sans-serif';

function getNameFontSize(name) {
  const len = (name || '').length;
  if (len <= 16) return '14px';
  if (len <= 22) return '11px';
  return '9.5px';
}

function BulkAdmitCardPrint({ user, onClose }) {
  const [students, setStudents]                   = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [selectedClass, setSelectedClass]         = useState('All');
  const [selectedSection, setSelectedSection]     = useState('All');
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [examName, setExamName]                   = useState('ANNUAL EXAMINATION');
  const [customExam, setCustomExam]               = useState('');
  const [academicSession, setAcademicSession]     = useState('2026-2027');
  const [sortBy, setSortBy]                       = useState('rollNumber');
  const [downloading, setDownloading]             = useState(false);
  const [searchTerm, setSearchTerm]               = useState('');
  const [printDate, setPrintDate]                 = useState(new Date().toISOString().split('T')[0]);
  const [cardOverrides, setCardOverrides]         = useState({});
  const [printMode, setPrintMode]                 = useState('mono');

  const printableRef = useRef(null);

  useEffect(() => { fetchStudents(); }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/users`);
      const list = (res.data || []).filter(u => u.role === 'student');
      setStudents(list);
      setSelectedStudentIds(new Set(list.map(s => s._id)));
    } catch (err) {
      console.error('Failed to load students', err);
    } finally {
      setLoading(false);
    }
  };

  const availableClasses   = ['All', ...Array.from(new Set(students.map(s => s.class).filter(Boolean))).sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0))];
  const availableSections  = ['All', ...Array.from(new Set(students.map(s => s.section).filter(Boolean))).sort()];

  const filteredStudents = students.filter(s => {
    if (selectedClass   !== 'All' && String(s.class)   !== String(selectedClass))   return false;
    if (selectedSection !== 'All' && String(s.section) !== String(selectedSection)) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (!(s.name || '').toLowerCase().includes(q) && !String(s.rollNumber || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const studentsToPrint = filteredStudents
    .filter(s => selectedStudentIds.has(s._id))
    .sort((a, b) => {
      if (sortBy === 'rollNumber') {
        const rA = parseInt(a.rollNumber) || 999999;
        const rB = parseInt(b.rollNumber) || 999999;
        if (rA !== rB) return rA - rB;
      }
      return (a.name || '').localeCompare(b.name || '');
    });

  const pages = [];
  for (let i = 0; i < studentsToPrint.length; i += 10) {
    const slice = studentsToPrint.slice(i, i + 10);
    const padded = [...slice];
    while (padded.length < 10) padded.push(null);
    pages.push(padded);
  }

  const toggleStudent      = (id) => { const n = new Set(selectedStudentIds); n.has(id) ? n.delete(id) : n.add(id); setSelectedStudentIds(n); };
  const selectAllFiltered  = ()   => { const n = new Set(selectedStudentIds); filteredStudents.forEach(s => n.add(s._id));    setSelectedStudentIds(n); };
  const deselectAllFiltered= ()   => { const n = new Set(selectedStudentIds); filteredStudents.forEach(s => n.delete(s._id)); setSelectedStudentIds(n); };
  const updateOverride     = (sid, field, val) => setCardOverrides(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [field]: val } }));

  const activeExamTitle = (customExam.trim() || examName).toUpperCase();
  const isMono          = printMode === 'mono';

  const handleDirectPrint = () => window.print();

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF }   = await import('jspdf');

      // Wait for all fonts to be fully loaded before rasterising
      await document.fonts.ready;

      const printEl  = printableRef.current;
      if (!printEl) return;
      const pageDoms = printEl.querySelectorAll('.admit-card-page');
      if (!pageDoms || pageDoms.length === 0) return;

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      for (let idx = 0; idx < pageDoms.length; idx++) {
        const dom    = pageDoms[idx];
        const canvas = await html2canvas(dom, {
          scale: 2.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          letterRendering: true,
          scrollX: 0,
          scrollY: 0,
          // onclone: force every student-name element to be solid black Arial
          onclone: (clonedDoc) => {
            // Fix student names
            clonedDoc.querySelectorAll('.student-name').forEach(el => {
              el.style.color       = '#000000';
              el.style.fontFamily  = 'Arial, sans-serif';
              el.style.fontStyle   = 'normal';
              el.style.visibility  = 'visible';
              el.style.display     = 'block';
              el.style.minHeight   = '16px';
            });
            // Fix all text nodes inside cards
            clonedDoc.querySelectorAll('.admit-card-inner *').forEach(el => {
              el.style.fontFamily = 'Arial, sans-serif';
              el.style.fontStyle  = 'normal';
            });
          }
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        if (idx > 0) pdf.addPage('a4', 'portrait');
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
      }

      const safeClass = selectedClass === 'All' ? 'School' : `Class_${selectedClass}`;
      const safeExam  = activeExamTitle.replace(/\s+/g, '_').substring(0, 20);
      const blob      = pdf.output('blob');
      const url       = URL.createObjectURL(blob);
      const a         = document.createElement('a');
      a.href          = url;
      a.download      = `AdmitCards_${safeClass}_${safeExam}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert('PDF generation failed: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.92)', zIndex: 10000, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{`
        /* Force system font on every card element — prevents blank-text bug */
        .admit-card-inner, .admit-card-inner * {
          font-family: Arial, "Helvetica Neue", Helvetica, sans-serif !important;
          font-style: normal !important;
        }
        .student-name {
          font-family: Arial, sans-serif !important;
          font-weight: 800 !important;
          color: #000000 !important;
          text-transform: uppercase !important;
          display: block !important;
          min-height: 18px !important;
          line-height: 1.2 !important;
          white-space: normal !important;
          word-break: break-word !important;
          margin: 2px 0 !important;
          font-style: normal !important;
          visibility: visible !important;
        }
        @media print {
          body * { visibility: hidden !important; }
          #admit-card-print-root, #admit-card-print-root * { visibility: visible !important; }
          #admit-card-print-root {
            position: absolute !important; left: 0 !important; top: 0 !important;
            width: 210mm !important; margin: 0 !important; padding: 0 !important;
            background: #ffffff !important;
          }
          .admit-card-page {
            width: 210mm !important; height: 297mm !important;
            margin: 0 !important; padding: 6mm !important;
            box-sizing: border-box !important;
            page-break-after: always !important; break-after: page !important;
            background: #ffffff !important;
          }
          @page { size: A4 portrait; margin: 0mm; }
          .no-print { display: none !important; }
          [contenteditable] { outline: none !important; }
        }
      `}</style>

      {/* HEADER */}
      <div className="no-print" style={{ background: '#000', color: '#fff', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #333', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ background: '#fff', color: '#000', fontWeight: 900, fontSize: 11, padding: '4px 10px', borderRadius: 4, letterSpacing: 1 }}>10-IN-1 A4</div>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900, fontFamily: SAFE_FONT }}>BULK ADMIT CARD GENERATOR</h2>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>2×5 grid · 95mm×52mm · contentEditable fields · fonts.ready PDF</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', background: '#1f2937', borderRadius: 6, overflow: 'hidden', border: '1px solid #374151' }}>
            <button onClick={() => setPrintMode('mono')}  style={{ padding: '7px 12px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background: isMono  ? '#fff'    : 'transparent', color: isMono  ? '#000' : '#9ca3af' }}>⬛ MONO</button>
            <button onClick={() => setPrintMode('color')} style={{ padding: '7px 12px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background: !isMono ? '#0f2e5a' : 'transparent', color: !isMono ? '#fff' : '#9ca3af' }}>🎨 COLOR</button>
          </div>
          <button onClick={handleDirectPrint} style={{ background: '#fff', color: '#000', border: 'none', borderRadius: 6, padding: '8px 14px', fontWeight: 900, fontSize: 11, cursor: 'pointer' }}>
            🖨️ PRINT ({studentsToPrint.length} / {pages.length}pp)
          </button>
          <button onClick={handleDownloadPDF} disabled={downloading} style={{ background: downloading ? '#555' : '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontWeight: 900, fontSize: 11, cursor: downloading ? 'not-allowed' : 'pointer' }}>
            {downloading ? '⏳ GENERATING...' : '⬇️ EXPORT PDF'}
          </button>
          <button onClick={onClose} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', fontWeight: 900, fontSize: 11, cursor: 'pointer' }}>✕</button>
        </div>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* SIDEBAR */}
        <div className="no-print" style={{ width: 340, background: '#111827', color: '#e5e7eb', padding: '12px 14px', overflowY: 'auto', borderRight: '1px solid #374151', display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>

          <div style={{ background: '#1f2937', padding: 11, borderRadius: 8, border: '1px solid #374151' }}>
            <div style={{ fontSize: 10, fontWeight: 900, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>1. Filter</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 7 }}>
              <div>
                <label style={{ fontSize: 10, color: '#d1d5db', display: 'block', marginBottom: 2 }}>Class</label>
                <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} style={{ width: '100%', padding: '5px 7px', borderRadius: 5, background: '#111827', color: '#fff', border: '1px solid #4b5563', fontSize: 11 }}>
                  {availableClasses.map(c => <option key={c} value={c}>{c === 'All' ? 'All Classes' : `Class ${c}`}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, color: '#d1d5db', display: 'block', marginBottom: 2 }}>Section</label>
                <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)} style={{ width: '100%', padding: '5px 7px', borderRadius: 5, background: '#111827', color: '#fff', border: '1px solid #4b5563', fontSize: 11 }}>
                  {availableSections.map(s => <option key={s} value={s}>{s === 'All' ? 'All Sections' : `Sec ${s}`}</option>)}
                </select>
              </div>
            </div>
            <input type="text" placeholder="🔍 Search name / roll..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ width: '100%', padding: '5px 9px', borderRadius: 5, background: '#111827', color: '#fff', border: '1px solid #4b5563', fontSize: 11, boxSizing: 'border-box' }} />
          </div>

          <div style={{ background: '#1f2937', padding: 11, borderRadius: 8, border: '1px solid #374151' }}>
            <div style={{ fontSize: 10, fontWeight: 900, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>2. Exam Details</div>
            <div style={{ marginBottom: 7 }}>
              <label style={{ fontSize: 10, color: '#d1d5db', display: 'block', marginBottom: 2 }}>Preset</label>
              <select value={examName} onChange={e => setExamName(e.target.value)} style={{ width: '100%', padding: '5px 7px', borderRadius: 5, background: '#111827', color: '#fff', border: '1px solid #4b5563', fontSize: 11 }}>
                {EXAM_PRESETS.map(ex => <option key={ex} value={ex}>{ex}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 7 }}>
              <label style={{ fontSize: 10, color: '#d1d5db', display: 'block', marginBottom: 2 }}>Custom Override</label>
              <input type="text" placeholder="Override exam title..." value={customExam} onChange={e => setCustomExam(e.target.value)} style={{ width: '100%', padding: '5px 9px', borderRadius: 5, background: '#111827', color: '#fff', border: '1px solid #4b5563', fontSize: 11, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              <div>
                <label style={{ fontSize: 10, color: '#d1d5db', display: 'block', marginBottom: 2 }}>Session</label>
                <input type="text" value={academicSession} onChange={e => setAcademicSession(e.target.value)} style={{ width: '100%', padding: '5px 7px', borderRadius: 5, background: '#111827', color: '#fff', border: '1px solid #4b5563', fontSize: 11, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: '#d1d5db', display: 'block', marginBottom: 2 }}>Issue Date</label>
                <input type="date" value={printDate} onChange={e => setPrintDate(e.target.value)} style={{ width: '100%', padding: '4px 7px', borderRadius: 5, background: '#111827', color: '#fff', border: '1px solid #4b5563', fontSize: 10, boxSizing: 'border-box' }} />
              </div>
            </div>
          </div>

          <div style={{ background: '#1f2937', padding: 11, borderRadius: 8, border: '1px solid #374151' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
              <div style={{ fontSize: 10, fontWeight: 900, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase' }}>3. Students ({studentsToPrint.length}/{filteredStudents.length})</div>
              <div style={{ display: 'flex', gap: 5 }}>
                <button onClick={selectAllFiltered}   style={{ background: '#374151', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer' }}>All</button>
                <button onClick={deselectAllFiltered} style={{ background: '#374151', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer' }}>None</button>
              </div>
            </div>
            <div style={{ marginBottom: 7, display: 'flex', gap: 10 }}>
              <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}><input type="radio" name="sortBy" checked={sortBy === 'rollNumber'} onChange={() => setSortBy('rollNumber')} /> Roll No</label>
              <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}><input type="radio" name="sortBy" checked={sortBy === 'name'}       onChange={() => setSortBy('name')}       /> Name A-Z</label>
            </div>
            <div style={{ maxHeight: 190, overflowY: 'auto', background: '#111827', borderRadius: 5, padding: '3px 5px', border: '1px solid #374151' }}>
              {filteredStudents.length === 0 && <div style={{ padding: 10, textAlign: 'center', color: '#6b7280', fontSize: 10 }}>No students found</div>}
              {filteredStudents.map(s => (
                <label key={s._id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px', borderRadius: 3, cursor: 'pointer', fontSize: 10.5, background: selectedStudentIds.has(s._id) ? 'rgba(37,99,235,0.15)' : 'transparent', marginBottom: 1 }}>
                  <input type="checkbox" checked={selectedStudentIds.has(s._id)} onChange={() => toggleStudent(s._id)} />
                  <span style={{ color: '#9ca3af', fontFamily: 'monospace', minWidth: 22 }}>#{s.rollNumber || '-'}</span>
                  <span style={{ color: '#fff', fontWeight: 600, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                  <span style={{ color: '#6b7280', fontSize: 9 }}>{s.class || '-'}{s.section ? `-${s.section}` : ''}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ background: '#000', padding: 9, borderRadius: 5, border: '1px solid #374151', fontSize: 11, lineHeight: 1.7 }}>
            <div>📄 <b>Pages:</b> {pages.length} A4</div>
            <div>🎴 <b>Cards:</b> {studentsToPrint.length}</div>
            <div>✂️ <b>Blank slots:</b> {pages.length * 10 - studentsToPrint.length}</div>
            <div style={{ marginTop: 4, fontSize: 10, color: '#6b7280' }}>Mode: <b style={{ color: isMono ? '#fff' : '#60a5fa' }}>{isMono ? 'Monochrome B&W' : 'Corporate Color'}</b></div>
          </div>
        </div>


        {/* PREVIEW PANE */}
        <div style={{ flex: 1, background: '#4b5563', overflowY: 'auto', padding: '18px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
          <div className="no-print" style={{ color: '#e5e7eb', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>
            LIVE PREVIEW — CLICK ANY FIELD TO EDIT · {isMono ? 'MONOCHROME B&W' : 'CORPORATE COLOR'}
          </div>
          <div id="admit-card-print-root" ref={printableRef} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {pages.map((pageGroup, pageIndex) => (
              <div
                key={pageIndex}
                className="admit-card-page"
                style={{
                  width: '210mm', height: '297mm', background: '#ffffff',
                  boxSizing: 'border-box', padding: '6mm', margin: '0 auto',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 95mm)',
                  gridTemplateRows: 'repeat(5, 52mm)',
                  gap: '3mm',
                  justifyContent: 'center',
                  alignContent: 'center'
                }}
              >
                {pageGroup.map((student, cardIndex) => (
                  <SingleAdmitCard
                    key={student ? student._id : `empty-${pageIndex}-${cardIndex}`}
                    student={student}
                    examTitle={activeExamTitle}
                    academicSession={academicSession}
                    overrides={student ? (cardOverrides[student._id] || {}) : {}}
                    onOverride={student ? (field, val) => updateOverride(student._id, field, val) : null}
                    isMono={isMono}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// ── SINGLE ADMIT CARD (95mm × 52mm) ──
// Uses div+contentEditable instead of <input> so html2canvas captures text correctly.
function SingleAdmitCard({ student, examTitle, academicSession, overrides, onOverride, isMono }) {
  const photoInputRef = useRef(null);

  if (!student) {
    return (
      <div style={{
        width: '95mm', height: '52mm', boxSizing: 'border-box',
        border: '1px dashed #000', display: 'flex', alignItems: 'center',
        justifyContent: 'center', color: '#aaa', fontSize: '8px',
        fontFamily: SAFE_FONT, fontWeight: 700, letterSpacing: 1
      }}>
        [ BLANK SLOT ]
      </div>
    );
  }

  const displayName     = overrides.name        !== undefined ? overrides.name        : (student.name || '');
  const rawClass        = student.class    || '';
  const rawSection      = student.section  || '';
  const defaultClassSec = rawClass ? `CLASS: ${rawClass}${rawSection ? ` - ${rawSection}` : ''}` : '';
  const displayClassSec = overrides.classSection !== undefined ? overrides.classSection : defaultClassSec;
  const hasRoll         = student.rollNumber !== undefined && student.rollNumber !== null && String(student.rollNumber).trim() !== '';
  const displayRoll     = overrides.rollNumber   !== undefined ? overrides.rollNumber   : (hasRoll ? String(student.rollNumber) : '');
  const photoSrc        = overrides.photo || student.profilePicture || null;
  const nameFontSize    = getNameFontSize(displayName);

  const borderColor = isMono ? '#000000' : '#0f2e5a';
  const headerBg    = isMono ? '#ffffff' : '#0f2e5a';
  const headerColor = isMono ? '#000000' : '#ffffff';
  const accentColor = isMono ? '#000000' : '#0f2e5a';
  const photoFilter = 'grayscale(100%) contrast(130%)';

  const handlePhotoClick  = () => { if (photoInputRef.current) photoInputRef.current.click(); };
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { if (onOverride) onOverride('photo', ev.target.result); };
    reader.readAsDataURL(file);
  };

  // contentEditable div handler — fires onBlur to commit value
  const EditableDiv = ({ value, onChange, className, style, placeholder }) => (
    <div
      className={className}
      contentEditable
      suppressContentEditableWarning
      onBlur={e => onChange && onChange(e.currentTarget.textContent)}
      style={{
        outline: 'none',
        fontFamily: SAFE_FONT,
        fontStyle: 'normal',
        color: '#000000',
        background: 'transparent',
        minHeight: '14px',
        ...style
      }}
      data-placeholder={placeholder}
    >
      {value}
    </div>
  );

  return (
    <div
      className="admit-card-inner"
      style={{
        width: '95mm', height: '52mm', boxSizing: 'border-box',
        border: `1.5px solid ${borderColor}`,
        background: '#ffffff', color: '#000000',
        fontFamily: SAFE_FONT,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* HEADER */}
      <div style={{
        background: headerBg, color: headerColor,
        borderBottom: `1px solid ${borderColor}`,
        padding: '1.5mm 2.5mm',
        display: 'flex', alignItems: 'center', gap: '2mm',
        flexShrink: 0
      }}>
        <div style={{
          width: '7mm', height: '7mm', flexShrink: 0,
          border: `1px solid ${isMono ? '#000' : '#fff'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', background: '#fff'
        }}>
          <img
            src="/logo.png" alt="TRS"
            style={{ width: '100%', height: '100%', objectFit: 'contain', filter: photoFilter, WebkitFilter: photoFilter }}
            onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerText = 'TRS'; }}
          />
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '10px', fontWeight: 900, letterSpacing: '0.6px', lineHeight: 1.1, fontFamily: SAFE_FONT }}>THE RISING SCHOOL</div>
          <div style={{ fontSize: '6.5px', fontWeight: 700, letterSpacing: '0.2px', marginTop: '0.5px', fontFamily: SAFE_FONT }}>ADMIT CARD &bull; {examTitle}</div>
          <div style={{ fontSize: '5.5px', fontWeight: 400, marginTop: '0.3px', fontFamily: SAFE_FONT }}>SAVINA, UDAIPUR &bull; SESSION: {academicSession}</div>
        </div>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, display: 'flex', gap: '2mm', padding: '2mm 2.5mm 1.5mm', minHeight: 0 }}>

        {/* LEFT: Student Info */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '1mm', minWidth: 0 }}>

          {/* NAME — primary, uses .student-name class for onclone targeting */}
          <EditableDiv
            className="student-name"
            value={displayName}
            onChange={val => onOverride && onOverride('name', val)}
            style={{ fontSize: nameFontSize, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.2px', lineHeight: 1.2, wordBreak: 'break-word', whiteSpace: 'normal' }}
          />

          {/* CLASS / SECTION */}
          <EditableDiv
            value={displayClassSec}
            onChange={val => onOverride && onOverride('classSection', val)}
            style={{ fontSize: '9px', fontWeight: 700, color: accentColor }}
          />

          {/* ROLL NO */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <span style={{ fontSize: '7.5px', fontWeight: 700, fontFamily: SAFE_FONT, whiteSpace: 'nowrap', color: '#000', flexShrink: 0 }}>ROLL NO:</span>
            <EditableDiv
              value={displayRoll}
              onChange={val => onOverride && onOverride('rollNumber', val)}
              placeholder="________"
              style={{
                fontSize: '9px', fontWeight: 900, minWidth: '20mm',
                borderBottom: displayRoll ? 'none' : '1px solid #000',
                paddingBottom: displayRoll ? 0 : '1px'
              }}
            />
          </div>
        </div>

        {/* RIGHT: Photo */}
        <div
          onClick={handlePhotoClick}
          title="Click to upload/change photo"
          style={{
            width: '24mm', minWidth: '24mm', height: '30mm',
            border: `1px solid ${borderColor}`,
            flexShrink: 0, alignSelf: 'center',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', cursor: 'pointer', background: '#f0f0f0'
          }}
        >
          {photoSrc ? (
            <img src={photoSrc} alt="student" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: photoFilter, WebkitFilter: photoFilter }} />
          ) : (
            <div style={{ textAlign: 'center', fontSize: '5px', fontWeight: 700, color: '#666', lineHeight: 1.5, fontFamily: SAFE_FONT }}>AFFIX<br />PHOTO</div>
          )}
          <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ borderTop: `1px solid ${borderColor}`, padding: '1mm 2.5mm', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexShrink: 0 }}>
        <div style={{ textAlign: 'center', width: '27mm' }}>
          <div style={{ borderBottom: `0.5px solid ${borderColor}`, height: '3.5mm', marginBottom: '0.5px' }} />
          <div style={{ fontSize: '5.5px', fontWeight: 700, fontFamily: SAFE_FONT }}>Class Teacher</div>
        </div>
        <div style={{ fontSize: '5px', color: '#000', textAlign: 'center', fontFamily: SAFE_FONT }}>Must carry daily</div>
        <div style={{ textAlign: 'center', width: '27mm' }}>
          <div style={{ borderBottom: `0.5px solid ${borderColor}`, height: '3.5mm', marginBottom: '0.5px' }} />
          <div style={{ fontSize: '5.5px', fontWeight: 700, fontFamily: SAFE_FONT }}>Principal</div>
        </div>
      </div>

    </div>
  );
}

export default BulkAdmitCardPrint;
